import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Collaboration } from '@pncl/common-models';
import { Mario } from '@pncl/mario-models';
import { captureException } from '@sentry/angular';
import { Mutex } from 'async-mutex';
import { BehaviorSubject, combineLatest, filter, first, fromEvent, map, take } from 'rxjs';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { UploadFileService } from 'src/app/services/upload-file.service';
import { EventStateManagerService } from 'src/app/services/event-state-manager.service';
import { matchI } from 'ts-adt';
import * as Y from 'yjs';
import { AppIcon, AppIconSource, ItemsCanvasService } from 'src/app/services/items-canvas.service';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { RealtimeSpaceService } from '../../../services/realtime-space.service';
import { ClientSyncProvider } from '../../../utilities/ClientSyncProvider';
import { ClientEventProvider } from '../../../utilities/ClientEventProvider';
import { SessionSharedDataService } from '../../../services/session-shared-data.service';
import { UserService } from '../../../services/user.service';
import { MarioService } from '../../../mario.service';
import {
  AppConfigurationService,
  CollaborativeApps,
  IAppInformation,
} from '../iframe/additional-apps.utils';
import { UiService } from '../../../services/ui.service';
import { RtcServiceController } from '../../../services/rtc.service';
import { modifiedSetTimeout } from '../../../utilities/ZoneUtils';
import { canApplyMarioUpdate, hasYDocAccess } from '../../../utilities/mario-ydoc.utils';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { DeviceAndBrowserDetectorService } from '../../../services/device-and-browser-detector.service';
import { AppsInteractionsLockService } from '../item-wrapper/apps-interactions-lock.service';
import { BoardItemsInteractionsManager } from '../items-canvas/board-items-interactions-manager';
import { ItemModel } from '../../../models/itemModel';
import { BoardMarioConfigurations } from '../items-canvas/board-items/board-mario-configurations';
import { ConfirmationModalComponent } from '../../../standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { AppsInsertionService } from '../items-canvas/board-items/apps-insertion.service';
import { BoardItemsManager } from '../items-canvas/board-items/board-items-manager';
import { SessionAuth } from '../../../models/session-sync';
import { FullScreenAppManagerService } from '../services/full-screen-app-manager.service';
import ADTUtil = Collaboration.Utils.ADTUtil;
import EventProvider = Collaboration.EventProvider;

/**
 * Mario apps upload whole documents (e.g. a presentation with its media), so
 * they get more room than the board's own uploads (MAX_FILE_SIZE_ALLOWED_MB).
 */
const MAX_MARIO_ASSET_SIZE_MB = 500;

type ClientMessage = MessageEvent<
  { marioId: Mario.MarioId; event: Mario.ClientMessages } | undefined
>;
@UntilDestroy()
@Component({
  selector: 'app-mario',
  templateUrl: './mario.component.html',
  styleUrls: ['./mario.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarioComponent implements OnChanges {
  @Input() state!: BoardMarioConfigurations;
  @Input() static!: boolean;

  @Output() stateChanged = new EventEmitter<BoardMarioConfigurations>();
  @Output() deleteItem = new EventEmitter<void>();
  @Output() interactionsLockChanged = new EventEmitter<boolean>();
  @Output() privacyChanged = new EventEmitter<boolean>();
  @Output() screenshotTaken = new EventEmitter<void>();
  @Output() availabilityChanged = new EventEmitter<boolean>();

  @ViewChild('marioIFrame') _iframeEl?: ElementRef<HTMLIFrameElement>;

  readonly shouldDisableInteractions$ = new BehaviorSubject<boolean>(false);
  readonly selected$ = new BehaviorSubject<boolean>(false);
  readonly isReloading$ = new BehaviorSubject<boolean>(false);
  readonly showLoadingIndicator$ = new BehaviorSubject<boolean>(true);
  readonly appIcon$ = new BehaviorSubject<AppIcon>({
    type: AppIconSource.ICON,
    content: '',
    height: 0,
    width: 0,
  });
  readonly showFullScreenButton$ = new BehaviorSubject<boolean>(true);

  protected readonly ItemModel = ItemModel;

  private yDoc = new Y.Doc();
  private syncProvider?: ClientSyncProvider;
  private eventProvider!: ClientEventProvider;
  private eventMutex = new Mutex();
  private _boundSendYjsUpdate?: (u: Uint8Array) => void;

  private spaceUserInfo$: BehaviorSubject<Mario.SpaceUsersInfo | undefined> = new BehaviorSubject<
    Mario.SpaceUsersInfo | undefined
  >(undefined);

  private _origin?: string;

  shouldRunAppOnMobile?: boolean;

  iframeLoaded = false;
  // Used when the iframe has not been loaded yet.
  private messageQueue: Mario.ServerMessages[] = [];

  /**
   * Whether this is an agent-generated (untrusted) app — drives the static-`sandbox`
   * iframe branch in the template (Angular NG0910 forbids binding `sandbox`).
   * `allow-same-origin` is intentional: an opaque-origin frame would post origin
   * "null" and fail the container's origin check (see `origin` getter); isolation
   * comes from the per-app runner origin, not the sandbox.
   *
   * Memoized (not a getter) so OnPush change detection reads a plain field instead
   * of recomputing every cycle. The resource `_type` is immutable for a board
   * item's lifetime (a refine changes contentHash, never _type), so it only needs
   * recomputing when the `state` input is (re)bound — see ngOnChanges.
   */
  isGeneratedApp = false;

  get origin(): string {
    if (!this._origin) {
      this._origin = new URL(this.state.url).origin;
    }
    return this._origin;
  }

  get auth(): SessionAuth & { userId: string } {
    const userId = this.userService.user.getValue()?.user._id;

    if (!userId) {
      throw new Error('User has not been set in the user service');
    }

    return {
      ...this.sharedDataService.sessionAuthData[this.state.spaceId].sessionAuth,
      userId,
    };
  }

  readonly isFullscreenAppInCall$ = this.fullScreenAppManagerService.isFullScreeningAppInCall$();

  constructor(
    private realtimeSpaceService: RealtimeSpaceService,
    private userService: UserService,
    private sharedDataService: SessionSharedDataService,
    private marioService: MarioService,
    public uiService: UiService,
    public rtcServiceController: RtcServiceController,
    private flagsService: FlagsService,
    public appsInteractionsLockService: AppsInteractionsLockService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private itemsCanvasService: ItemsCanvasService,
    public appConfigurationService: AppConfigurationService,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    private matDialog: MatDialog,
    private translate: TranslateService,
    private appsInsertionService: AppsInsertionService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private eventStateManagerService: EventStateManagerService,
    private uploadFileService: UploadFileService,
    private fullScreenAppManagerService: FullScreenAppManagerService,
  ) {
    this.marioService.marioStateChange$
      .pipe(untilDestroyed(this))
      .subscribe(({ contentId, state }) => {
        if (contentId === this.state?.contentId) {
          this._enqueueMessage({
            _type: 'SetState',
            state,
          });
        }
      });
  }

  ngOnInit(): void {
    if (this.static) {
      return;
    }

    this.shouldRunAppOnMobile = this.getShouldRunOnMobile();
    if (!this.shouldRunAppOnMobile) {
      return;
    }

    this.setUpInteractionsLock();

    const socket = this.realtimeSpaceService.service.socket;

    if (!socket) {
      throw new Error('No socket when creating Mario component');
    }

    this.syncProvider = new ClientSyncProvider(
      socket,
      this.yDoc,
      {
        _type: 'Mario',
        marioId: this.state.marioId,
        spaceId: this.state.spaceId,
        auth: this.auth,
      },
      this.flagsService.featureFlagsVariables.limit_missing_update_sync
        ?.pending_structs_sync_threshold as number | undefined,
    );
    this.syncProvider.subscribe();

    this.eventProvider = new ClientEventProvider(socket);

    this._setupIframeListener();

    combineLatest([this.marioService.users$, this.marioService.currentUser$])
      .pipe(
        untilDestroyed(this),
        map(([users, currentUserId]) => ({
          users,
          currentUserId,
          ownerId: this.state.ownerId,
          spaceId: this.state.spaceId,
        })),
      )
      .subscribe((spaceUserInfo: Mario.SpaceUsersInfo) => {
        this.spaceUserInfo$.next(spaceUserInfo);
        this._enqueueMessage({
          _type: 'SpaceContext',
          spaceContext: this.getSpaceContext(),
        });
      });

    this.showFullScreenButton$.next(
      this.state.appInstanceId !== CollaborativeApps.STOPWATCH &&
        this.state.appInstanceId !== CollaborativeApps.TIMER,
    );
  }

  ngAfterViewInit() {
    // The app may be missing from this viewer's app list (e.g. a library app not assigned
    // to their institution); fall back to the generic Mario icon.
    const app = this.appConfigurationService.getAppByUid(this.state.appInstanceId);
    const icon = app?.icon ?? 'code_iframe';
    this.appIcon$.next({
      type: this.itemsCanvasService.getIconSource(icon),
      content: icon,
      height: 32,
      width: 32,
    });

    this.boardItemsInteractionsManager
      .itemSelected$(this.state.uid)
      .pipe(untilDestroyed(this))
      .subscribe((selected) => {
        this.selected$.next(selected);
        this.cdr.detectChanges();
      });

    this.appsInteractionsLockService.onInteractionsLockChanged(
      this.state.interactionsLocked,
      this.state.uid,
    );
  }

  private getEventScope(eventId: string): ADTUtil.ExtractADT<EventProvider.Scope, 'Mario'> {
    return {
      _type: 'Mario',
      spaceId: this.state.spaceId,
      marioId: this.state.marioId,
      auth: this.auth,
      eventId,
    };
  }

  /**
   * Listen for messages from the iframe
   */
  private _setupIframeListener() {
    fromEvent<ClientMessage>(window, 'message')
      .pipe(
        filter(this._validateSource.bind(this)),
        filter(this._validateOrigin.bind(this)),
        filter(this._validateMarioId.bind(this)),
        untilDestroyed(this),
      )
      .subscribe(this._receiveMessageFromClient.bind(this));
  }

  private _validateOrigin(e: ClientMessage) {
    return e.origin === this.origin;
  }

  /**
   * Validate that the message is destined for this component
   * @param e
   * @returns
   */
  private _validateMarioId(e: ClientMessage) {
    if (
      e.data?.marioId === this.state.marioId ||
      e.data?.event?._type === 'RequestSpaceContext' // This event will not have an id
    ) {
      return true;
    }
    return false;
  }

  private _validateSource(e: ClientMessage) {
    return e.source === this._iframeEl?.nativeElement.contentWindow;
  }

  private _enqueueMessage(event: Mario.ServerMessages) {
    const contentWindow = this._iframeEl?.nativeElement?.contentWindow;
    if (this.messageQueue.length === 0 && contentWindow && this.iframeLoaded) {
      contentWindow.postMessage(event, this.origin);
    } else {
      this.messageQueue.push(event);
    }
  }

  /**
   * Send a message to the client, this should only be used when we know that the iframe has been loaded
   * @param event
   */
  private _sendMessageToClient(event: Mario.ServerMessages) {
    this._iframeEl?.nativeElement?.contentWindow?.postMessage(event, this.origin);
  }

  /**
   * When messages are received from the client, process them according to the type
   * @param event
   * @returns
   */
  private _receiveMessageFromClient(event: ClientMessage) {
    if (!event?.data?.event) {
      return;
    }

    matchI(event.data.event)({
      YJSUpdate: this._handleYjsUpdate.bind(this),
      UserEvent: this._handleUserEvent.bind(this),
      Subscribe: this._handleSubscribe.bind(this),
      Unsubscribe: this._handleUnsubscribe.bind(this),
      Ready: this._handleReady.bind(this),
      UploadAsset: this._handleUploadAsset.bind(this),
      RequestSpaceContext: () => {
        this._enqueueMessage({ _type: 'SpaceContext', spaceContext: this.getSpaceContext() });
      },
    });
  }

  /**
   * Upload an asset (e.g. an image) on the Mario app's behalf, since the iframe
   * has no Firebase auth of its own. We upload as the current user via the same
   * flow the board uses and reply with the hosted URL (or a failure). This keeps
   * bytes out of the size-capped shared state — the app stores just the URL.
   */
  private _handleUploadAsset(message: ADTUtil.ExtractADT<Mario.ClientMessages, 'UploadAsset'>) {
    const { requestId, fileName, mimeType, data } = message;

    // Give the common failure (too large) a real reason the app can surface;
    // UploadFileService enforces the same limit, but its onFailure carries none.
    const maxBytes = MAX_MARIO_ASSET_SIZE_MB * 1024 * 1024;
    if (data.byteLength > maxBytes) {
      this._enqueueMessage({
        _type: 'AssetUploadResult',
        requestId,
        success: false,
        error: Mario.AssetUploadError.TooLarge,
      });
      return;
    }

    const file = new File([data], fileName || requestId, { type: mimeType });
    this.uploadFileService.uploadToFireStorage(
      file,
      (url) =>
        this._enqueueMessage({ _type: 'AssetUploadResult', requestId, success: true, url }),
      () =>
        this._enqueueMessage({
          _type: 'AssetUploadResult',
          requestId,
          success: false,
          error: Mario.AssetUploadError.Failed,
        }),
      false,
      MAX_MARIO_ASSET_SIZE_MB,
    );
  }

  private get appInformation(): IAppInformation | undefined {
    return this.appConfigurationService.getAppByUid(this.state.appInstanceId);
  }

  private getSpaceContext(): Mario.SpaceContext {
    const spaceUsersInfo = this.spaceUserInfo$.getValue();
    if (!spaceUsersInfo) {
      throw new Error('Can not get the user information for the context');
    }
    return {
      marioId: this.state.marioId,
      spaceUsersInfo,
      spaceSessionInfo: this.getSessionContext(),
      hasYDocAccess: hasYDocAccess(this.appInformation),
    };
  }

  private getSessionContext(): Mario.SpaceSessionInfo {
    const sessionId = this.analyticsService.getCurrentSessionId();
    if (!sessionId) {
      captureException(new Error('No session id found'));
    }
    return {
      spaceId: this.state.spaceId,
      sessionId: sessionId || '', // fallback to empty string
      eventId: this.eventStateManagerService.activeEvent?._id,
    };
  }

  private _handleYjsUpdate(yjsUpdate: ADTUtil.ExtractADT<Mario.ClientMessages, 'YJSUpdate'>) {
    // Max size is 2MB
    const maxUpdateSize = 2e6;
    if (yjsUpdate.update.byteLength > maxUpdateSize) {
      throw new Error(`Size of update from Mario App (${this.state.marioId}) is too large`);
    }

    // Only Pencil Mario apps may write outside the JSON root map.
    if (!canApplyMarioUpdate(this.appInformation, yjsUpdate.update)) {
      console.warn(`Mario App (${this.state.marioId}) sent a non-JSON Yjs update; dropped`);
      return;
    }

    Y.applyUpdate(this.yDoc, yjsUpdate.update);
  }

  /**
   * Send events to the server to be sent to all other Mario Clients
   * @param userEvent
   */
  private _handleUserEvent(userEvent: ADTUtil.ExtractADT<Mario.ClientMessages, 'UserEvent'>) {
    this.eventProvider.sendEvent({
      _type: 'Event',
      scope: {
        _type: 'Mario',
        spaceId: this.state.spaceId,
        marioId: this.state.marioId,
        eventId: userEvent.userEvent.eventId,
      },
      data: userEvent.userEvent.data,
    });
  }

  /**
   * Subscribe to events for a given event id
   * @param subscribe
   */
  private _handleSubscribe(subscribe: ADTUtil.ExtractADT<Mario.ClientMessages, 'Subscribe'>) {
    const scope = this.getEventScope(subscribe.eventId);

    this.eventMutex.runExclusive(async () => {
      await this.eventProvider.subscribe(scope);
    });

    // Send the messages to the mario client
    this.eventProvider.on(this.eventProvider.eventNameForScope(scope), (data: unknown) => {
      this._enqueueMessage({
        _type: 'UserEvent',
        userEvent: {
          eventId: subscribe.eventId,
          data,
        },
      });
    });
  }

  /**
   * Unsubscribe to events for a given eventId
   * @param unsubscribe
   */
  private _handleUnsubscribe(unsubscribe: ADTUtil.ExtractADT<Mario.ClientMessages, 'Unsubscribe'>) {
    this.eventMutex.runExclusive(async () => {
      await this.eventProvider.unsubscribe(this.getEventScope(unsubscribe.eventId));
    });
  }

  /**
   * When the client sends the ready message we send it the YJS data,
   * and continue to update the client whenever the Y.Doc is updated
   */
  private _handleReady() {
    // Wait for the sync provider to sync the document, once it has then we send the first update
    // to the client. After that subsequent updates will be handled using the update listener
    this.syncProvider?.synced.pipe(first((v) => v)).subscribe(() => {
      // we are synced so we can send the update
      const update = Y.encodeStateAsUpdate(this.yDoc);
      this._enqueueMessage({
        _type: 'YJSUpdate',
        update,
      });

      if (!this._boundSendYjsUpdate) {
        this._boundSendYjsUpdate = this._sendYjsUpdate.bind(this);
        this.yDoc.on('update', this._boundSendYjsUpdate);
      }

      const initialState = this.marioService.getInitialState(this.state.contentId);
      if (initialState) {
        this._enqueueMessage({
          _type: 'SetState',
          state: initialState,
        });
      }
      this.marioService.clearInitialState(this.state.contentId);
    });
  }

  private _sendYjsUpdate(u: Uint8Array) {
    this._enqueueMessage({
      _type: 'YJSUpdate',
      update: u,
    });
  }

  ngOnDestroy(): void {
    this.syncProvider?.unsubscribe();
    if (this._boundSendYjsUpdate) {
      this.yDoc.off('update', this._boundSendYjsUpdate);
    }
  }

  getShouldRunOnMobile(): boolean {
    return !(
      (this.deviceAndBrowserDetectorService.isMobile() &&
        this.flagsService.isFlagEnabled(FLAGS.DISABLE_APPS_ON_MOBILE) &&
        !this.isForcedToBeAvailableOnMobile(this.state.appInstanceId)) ??
      false
    );
  }

  private setUpInteractionsLock() {
    this.appsInteractionsLockService
      .interactionsLockState$(this.state.uid, this.state.interactionsLocked)
      .pipe(untilDestroyed(this))
      .subscribe((interactionsLockState) => {
        // Those are not all the errors, but are all the errors that the user should try to recover from them by themselves
        this.shouldDisableInteractions$.next(interactionsLockState.interactionsDisabledForUser);
        if (this.shouldDisableInteractions$.getValue()) {
          this._iframeEl?.nativeElement?.blur && this._iframeEl.nativeElement.blur();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.state) {
      this.isGeneratedApp = this.state?.resource?._type === 'GENERATED';
    }

    const previous = changes.state?.previousValue as BoardMarioConfigurations;
    const current = changes.state?.currentValue as BoardMarioConfigurations;

    if (previous && current && previous.interactionsLocked !== current.interactionsLocked) {
      this.appsInteractionsLockService.onInteractionsLockChanged(
        this.state.interactionsLocked,
        this.state.uid,
      );
    }
  }

  onReload() {
    this.showLoadingIndicator$.next(true);
    this.hideLoadingIndicator();

    // This following lines are not the original behavior of reloading the mario app,
    // but it is how it should once we fix the problems of this method
    // this.isReloading$.next(true);
    // modifiedSetTimeout(() => this.isReloading$.next(false), 50);
  }

  onMarioLoaded() {
    this.iframeLoaded = true;

    this.messageQueue.forEach((message) => {
      this._sendMessageToClient(message);
    });
    this.messageQueue = [];

    this.hideLoadingIndicator();
  }

  protected hideLoadingIndicator(): void {
    modifiedSetTimeout(() => this.showLoadingIndicator$.next(false), 1500);
  }

  isForcedToBeAvailableOnMobile(appId?: string): boolean {
    if (!appId) {
      return false;
    }
    return this.appConfigurationService.getAppByUid(appId)?.forceAvailableOnMobile || false;
  }

  onInteractionsLockChanges(interactionsLocked: boolean): void {
    this.state.interactionsLocked = interactionsLocked;
    this.appsInteractionsLockService.onInteractionsLockChanged(interactionsLocked, this.state.uid);
    this.emitChanges();
  }
  onPrivacyChanged(isPrivate: boolean): void {
    this.state.isPrivate = isPrivate;
    this.emitChanges();
  }
  onDeleteItem() {
    this.deleteItem.emit();
  }

  emitChanges() {
    this.stateChanged.emit(this.state);
  }

  handleReset() {
    this.matDialog
      .open(ConfirmationModalComponent, {
        data: {
          title: this.translate.instant('Reset App?'),
          description: this.translate.instant(
            'This will reset this app for everyone in this Space.',
          ),
          confirmButtonConfig: {
            icon: 'check',
            iconType: 'mat-icon',
            label: this.translate.instant('Continue'),
            danger: true,
          },
          cancelButtonConfig: {
            icon: 'close',
            iconType: 'mat-icon',
            label: this.translate.instant('Cancel'),
          },
        },
        panelClass: 'reset-app-modal',
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((output: 'confirm' | 'cancel') => {
        if (output === 'confirm') {
          this.marioService.setMarioState(this.state.contentId, {});
          // Pencil Mario apps also clear their whole Y.Doc.
          if (hasYDocAccess(this.appInformation)) {
            this._enqueueMessage({ _type: 'Reset' });
          }
        }
      });
  }

  handleDuplicate() {
    this.appsInsertionService.beforeAppInsertion(ItemModel.Mario, this.state.appInstanceId);
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.Mario>(
      ItemModel.Mario,
      {
        appInstanceId: this.state.appInstanceId,
        url: this.state.url,
        resource: this.state.resource,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );
  }

  protected readonly CollaborativeApps = CollaborativeApps;
}
